import type { ReactNode } from 'react';

import { ensureArray } from '@iconik-io/core/src/ensure_array';
import type { MetadataFieldType } from '@iconik-io/iconik-api/src/types/metadata_field_type';
import { guardMultiSelectFilter } from '@iconik-io/shared-lib/src/utils/search/guard_multi_select_filter';

import {
  getMetadataFieldAvailableModifiers,
  MULTI_SELECT_TEXT_FIELD_TYPES,
} from '../../constants/search_mappings';
import InputDate from '../../ui/forms/field_wrappers/date/date';
import { MultiSelectFilterDropDownField } from '../../ui/forms/field_wrappers/multi_select_filter_drop_down_field/multi_select_filter_drop_down_field';
import { MultiSelectFilterTagCloudField } from '../../ui/forms/field_wrappers/multi_select_filter_tag_cloud_field/multi_select_filter_tag_cloud_field';
import { MultiSelectFilterTextField } from '../../ui/forms/field_wrappers/multi_select_filter_text_field/multi_select_filter_text_field';
import SelectFieldWrapper from '../../ui/forms/field_wrappers/select/select';
import TextFieldWrapper from '../../ui/forms/field_wrappers/text/text';
import Separator from '../fields/separator/separator';
import type { MetadataSearchRendererFieldProps } from './types';

export type MetadataFieldSearchRenderer = (
  type: MetadataFieldType,
  fieldProps: MetadataSearchRendererFieldProps,
) => ReactNode;

const metadataFieldSearchRenderer: MetadataFieldSearchRenderer = (
  type,
  fieldProps,
) => {
  const props = {
    ...fieldProps,
    required: false,
    readOnly: false,
    help: undefined,
  };
  const nonMultiSelectValue = guardMultiSelectFilter(props.value, {
    convertLegacyStrings: false,
  })
    ? undefined
    : (props.value as string[] | string | undefined);
  const values = ensureArray(nonMultiSelectValue);
  const firstValue = values.length > 0 && values[0] ? values[0] : undefined;
  const booleanOptions = [
    { label: 'True', value: 'true' },
    { label: 'False', value: 'false' },
  ];
  const { dateFormat, dateTimeFormat } = props;

  // Text and numeric fields are rendered as a multi-select filter with
  // modifiers (include/exclude terms) whenever they are used as a search
  // filter, regardless of the field's `multi` flag.
  if (MULTI_SELECT_TEXT_FIELD_TYPES.has(type)) {
    const inputType =
      type === 'integer' ||
      type === 'float' ||
      type === 'email' ||
      type === 'url'
        ? type
        : 'string';
    return (
      <MultiSelectFilterTextField
        name={props.name}
        value={guardMultiSelectFilter(props.value)}
        onChange={props.onChange}
        label={props.label}
        subtitle="Items must..."
        vertical={props.vertical}
        disabled={props.readOnly}
        inputType={inputType}
      />
    );
  }

  switch (type) {
    case '__separator__':
      return <Separator content={props.content} />;
    case 'tag_cloud': {
      const multiSelectValue = guardMultiSelectFilter(props.value);
      return (
        <MultiSelectFilterTagCloudField
          name={props.name}
          value={multiSelectValue}
          onChange={props.onChange}
          label={props.label}
          subtitle="Items must..."
          vertical={props.vertical}
          disabled={props.readOnly}
        />
      );
    }
    case 'drop_down': {
      // Legacy single-select drop_down filters stored their value as a bare
      // string. Coerce it to an array so guardMultiSelectFilter normalizes it
      // into an include_any_of group instead of discarding it.
      const multiSelectValue = guardMultiSelectFilter(
        ensureArray<unknown>(props.value),
      );
      return (
        <MultiSelectFilterDropDownField
          name={props.name}
          value={multiSelectValue}
          onChange={props.onChange}
          label={props.label}
          subtitle="Items must..."
          vertical={props.vertical}
          disabled={props.readOnly}
          options={props.options ?? []}
          availableModifiers={getMetadataFieldAvailableModifiers(
            'drop_down',
            props.multi,
          )}
          maxRows={props.multi ? undefined : 1}
        />
      );
    }
    case 'date':
      return (
        <InputDate
          outputFormat="yyyy-MM-dd"
          {...props}
          value={firstValue}
          dateFormat={dateFormat}
        />
      );
    case 'datetime':
      return (
        <InputDate {...props} value={firstValue} dateFormat={dateTimeFormat} />
      );
    case 'boolean':
      return (
        <SelectFieldWrapper
          {...props}
          value={firstValue}
          options={booleanOptions}
          isMulti={false}
          canClear
        />
      );
    default:
      return <TextFieldWrapper {...props} value={firstValue} />;
  }
};

export default metadataFieldSearchRenderer;
